import classNames from 'classnames';
import { useState } from 'react';

import { StateMessage } from '@/components/ui/controls';
import { btn, field } from '@/components/ui/styles';
import { useIdentity } from '@/features/identity/identity';
import type { Role, Staff } from '@/types/api';
import http from '@/utils/http';
import { useApi } from '@/utils/use-api';

import SettingsHeader from './settings-nav';

type RowEdit = { name: string; email: string; role: Role; telegram_handle: string };

// The shared controls (44px tall on a phone, UI-1), sized for each row: on a phone it stacks.
const inputCls = classNames(field.input, 'md:w-auto md:flex-1');
const selectCls = classNames(field.select, 'w-full shrink-0 md:w-28');
const primaryCls = btn.primary;
const quietCls = btn.quiet;

/**
 * Staff admin, copied from spark-board's Members page: add someone by email, rename, set the
 * role, activate or deactivate. Only people on this list can sign in. Under SSO only admins
 * manage staff (the backend enforces it); everyone else gets a read-only list.
 */
const StaffPage = () => {
  const { me, oidc } = useIdentity();
  const canManage = !oidc || me?.role === 'admin';
  const { data: staff, error: loadError, reload } = useApi<Staff[]>('/staff');
  const [edits, setEdits] = useState<Record<string, RowEdit>>({});
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const val = (s: Staff): RowEdit => edits[s.id] ?? { name: s.name, email: s.email ?? '', role: s.role, telegram_handle: s.telegram_handle ?? '' };
  const patch = (s: Staff, p: Partial<RowEdit>) => setEdits((e) => ({ ...e, [s.id]: { ...val(s), ...p } }));

  const run = async (call: () => Promise<unknown>, after?: () => void) => {
    setBusy(true);
    setError(null);
    try {
      await call();
      after?.();
      reload();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      setError(
        status === 409
          ? 'Someone already has that email or Telegram username.'
          : status === 422
            ? 'Check the email address and the Telegram username (5 to 32 letters, digits or _).'
            : 'That did not work. Try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  const save = (s: Staff) =>
    run(
      () => http.put(`/staff/${s.id}`, val(s)),
      () =>
        setEdits((e) => {
          const n = { ...e };
          delete n[s.id];
          return n;
        }),
    );
  const toggleActive = (s: Staff) => run(() => http.put(`/staff/${s.id}`, { active: !s.active }));
  const add = () => {
    if (!newEmail.trim()) return;
    return run(
      () => http.post('/staff', { email: newEmail.trim(), name: newName.trim() }),
      () => {
        setNewEmail('');
        setNewName('');
      },
    );
  };

  return (
    <div className="max-w-4xl">
      <SettingsHeader />
      <p className="text-sm text-fg-muted">
        {canManage ? 'Who can sign in to Prisma, and who is an admin. Deactivated people can no longer sign in.' : 'Who can sign in to Prisma. Only admins can change this list.'}
      </p>

      <div className="mt-4 rounded-card bg-surface p-4 shadow-e1">
        {canManage ? (
          <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center">
            <input className={inputCls} type="email" placeholder="Email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
            <input className={inputCls} placeholder="Name (optional)" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
            <button type="button" className={primaryCls} disabled={busy || !newEmail.trim()} onClick={add}>
              Add
            </button>
          </div>
        ) : null}
        {error ? <div className="mb-3 text-sm text-danger">{error}</div> : null}

        {loadError && !staff ? <StateMessage title="Could not load the staff list" tone="error">{loadError}</StateMessage> : null}
        <div className="space-y-2">
          {(staff ?? []).map((s) =>
            canManage ? (
              <div
                key={s.id}
                className={classNames(
                  'grid grid-cols-2 gap-2 rounded-lg bg-surface-inset p-3 md:flex md:items-center md:rounded-none md:bg-transparent md:p-0',
                  !s.active && 'opacity-50',
                )}
              >
                <input className={classNames(inputCls, 'col-span-2')} placeholder="Name" value={val(s).name} onChange={(e) => patch(s, { name: e.target.value })} />
                <input className={classNames(inputCls, 'col-span-2')} type="email" placeholder="Email" value={val(s).email} onChange={(e) => patch(s, { email: e.target.value })} />
                <input
                  className={classNames(inputCls, 'col-span-2 md:max-w-40')}
                  placeholder="@telegram"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={val(s).telegram_handle}
                  onChange={(e) => patch(s, { telegram_handle: e.target.value })}
                  aria-label="Telegram username"
                  title="Their messages in a client group count as the team's"
                />
                <select className={classNames(selectCls, 'col-span-2')} value={val(s).role} onChange={(e) => patch(s, { role: e.target.value as Role })} aria-label="Role">
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
                <button type="button" className={quietCls} disabled={busy || !edits[s.id]} onClick={() => save(s)}>
                  Save
                </button>
                <button type="button" className={classNames(quietCls, 'md:w-24')} disabled={busy} onClick={() => toggleActive(s)}>
                  {s.active ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            ) : (
              <div key={s.id} className={classNames('flex flex-wrap items-center gap-x-3 gap-y-0.5 px-1 py-1.5 md:flex-nowrap md:gap-y-0', !s.active && 'opacity-50')}>
                <span className="text-sm text-fg-strong">{s.name}</span>
                {s.role === 'admin' ? (
                  <span className="rounded bg-brand/15 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-brand">Admin</span>
                ) : null}
                {s.email ? <span className="break-all text-xs text-fg-faint md:break-normal">{s.email}</span> : null}
                {s.telegram_handle ? <span className="text-xs text-fg-faint">@{s.telegram_handle}</span> : null}
                {!s.active ? <span className="text-xs text-fg-faint">(inactive)</span> : null}
              </div>
            ),
          )}
          {staff && staff.length === 0 ? <div className="py-6 text-center text-sm text-fg-faint">No staff yet.</div> : null}
        </div>
      </div>
    </div>
  );
};

export default StaffPage;
